import { Component, inject, Input, OnInit }     from '@angular/core';
import { takeUntilDestroyed }                   from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationError, NavigationSkipped, Router, RouterLink } from '@angular/router';
import { filter }                               from 'rxjs';
import { MatCard }                              from '@angular/material/card';
import { MatIcon }                              from '@angular/material/icon';
import { NgTemplateOutlet }                     from '@angular/common';

import { AccountDetails, AccountOwner, AccountsViewMode, AccountStatus, AccountType, ActStatus, ProductType } from '../../../../api/account-data/account-data';
import { TooltipComponent }                     from '../../tooltip/tooltip.component';
import { PinCurrencyPipe }                      from '../../../pipes/pin-currency.pipe';
import { PinDatePipe }                          from '../../../pipes/pin-date.pipe';
import { PinDecimalPipe }                       from '../../../pipes/pin-decimal.pipe';

const MASK_CHARACTER = '\u2022';
const MASK_LENGTH = 4;
const VISIBLE_DIGITS = 4;
const SEPARATOR = ' \u00B7 ';

@Component({
  selector: 'pin-accounts-list-card',
  templateUrl: './accounts-list-card.component.html',
  styleUrl: './accounts-list-card.component.scss',
  host: { '[class.list-view]': 'viewMode === accountsViewMode.LIST' },
  imports: [MatCard, MatIcon, NgTemplateOutlet, RouterLink, TooltipComponent, PinCurrencyPipe, PinDatePipe, PinDecimalPipe]
})
export class AccountsListCardComponent implements OnInit {
  @Input()
  data: AccountDetails;
  @Input()
  accountSummaryUrl: string;
  @Input()
  viewMode: AccountsViewMode = AccountsViewMode.CARD;

  accOwners: AccountOwner[] = [];
  otherOwners: string[] = [];
  otherAccountTitles: string[] = [];
  otherAccountTitlesText = '';
  maskedAccountNumber = '';
  accountStatus = AccountStatus;
  accountType = AccountType;
  accountsViewMode = AccountsViewMode;
  actStatus = ActStatus;
  productType = ProductType;
  pressed = false;
  private router = inject(Router);

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped),
        takeUntilDestroyed()
      )
      .subscribe(() => this.pressed = false);
  }

  ngOnInit(): void {
    this.getOwners();
    this.otherOwners = this.getOtherOwners();
    this.otherAccountTitles = this.data.accountTitles.slice(1).filter(Boolean);
    this.otherAccountTitlesText = this.otherAccountTitles.join(SEPARATOR);
    this.maskedAccountNumber = this.getMaskedAccountNumber();
  }

  getMaskedAccountNumber(): string {
    const accountNumber = this.isIcxAccount(this.data.accountType)
      ? this.data.transactionAccountNumber
      : this.data.accountId.toString();
    return MASK_CHARACTER.repeat(MASK_LENGTH) + accountNumber.slice(-VISIBLE_DIGITS);
  }

  getNumberOfOwners(): string {
    const prefix = this.accOwners.length - 1 > 1 ? 's' : '';
    return (this.accOwners.length - 1) !== 0 ? `+${this.accOwners.length - 1} owner${prefix}` : '';
  }

  getOwners(): void {
    this.accOwners = Object.values(this.data.accountOwners as Array<AccountOwner>);
    this.accOwners.sort((a, b) => a.name.localeCompare(b.name));
    this.accOwners.sort((a, b) => Number(b.primary) - Number(a.primary));
  }

  getOtherOwners(): string[] {
    const otherOwners = [];
    for (let i = 1; i < this.accOwners.length; i++) {
      otherOwners.push(this.accOwners[i].name);
    }
    return otherOwners;
  }

  onPress(): void {
    this.pressed = true;
  }

  redirectToAccountSummary() {
    this.pressed = true;
    this.router.navigate([this.accountSummaryUrl]).then(navigated => {
      if (!navigated) {
        this.pressed = false;
      }
    });
  }

  isIcxAccount(accountType: string): boolean {
    return [AccountType.SAVINGS.toUpperCase(), AccountType.DEMAND.toUpperCase()].includes(accountType.toUpperCase());
  }
}
